import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertCircle, Check, X, Download, Gift, Loader2, Percent, Send, Shield,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DialogClose, DialogContent } from '../components/ui/dialog';

// Circular import (named) — works because useAuth / usePWAInstall are only
// called inside render bodies, never at module top-level evaluation time.
// See Fase 3b decision (Pad B) in PRD.md.
import { useAuth, usePWAInstall } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AuthDialog + AuthDialogContent — Login/Register modal with email+password,
 * Google OAuth (direct + Emergent fallback), referral code, B2B pass code
 * validation, email-verification resend, and PWA install prompt.
 *
 * Extracted from App.js (Fase 3b) — pure relocation, no logic changes.
 */

// Auth Dialog Content - reusable for both trigger-based and controlled dialogs
export const AuthDialogContent = ({ defaultTab = "login", onClose }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(defaultTab);
  const { login, register, loginWithGoogle, loginWithGoogleDirect } = useAuth();
  const { canInstall, isInstalled, handleInstall } = usePWAInstall();
  const [loading, setLoading] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showVerificationError, setShowVerificationError] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", name: "", referralCode: "", b2bCode: "" });
  const [b2bValidation, setB2bValidation] = useState({ status: null, passType: null, message: "" });
  const [b2bTimeout, setB2bTimeout] = useState(null);
  const navigate = useNavigate();

  const validateB2bCode = useCallback(async (code) => {
    if (!code || code.trim().length < 3) { setB2bValidation({ status: null, passType: null, message: "" }); return; }
    setB2bValidation({ status: "loading", passType: null, message: "" });
    try {
      const res = await axios.post(`${API}/auth/validate-b2b-code`, { pass_code: code.trim() });
      setB2bValidation(res.data.valid ? { status: "valid", passType: res.data.pass_type, message: res.data.message } : { status: "invalid", passType: null, message: res.data.message });
    } catch { setB2bValidation({ status: "invalid", passType: null, message: "Validation failed" }); }
  }, []);

  const handleB2bCodeChange = (value) => {
    const upper = value.toUpperCase();
    setFormData(prev => ({ ...prev, b2bCode: upper }));
    if (b2bTimeout) clearTimeout(b2bTimeout);
    if (!upper || upper.length < 3) { setB2bValidation({ status: null, passType: null, message: "" }); return; }
    setB2bTimeout(setTimeout(() => validateB2bCode(upper), 600));
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: formData.email });
      toast.success(t('auth.verificationEmailSent', 'Verification email sent! Please check your inbox.'));
      setShowVerificationError(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.resendFailed', 'Failed to resend verification email'));
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowVerificationError(false);
    try {
      if (tab === "login") {
        await login(formData.email, formData.password);
        toast.success(t('auth.welcomeBack'));
      } else {
        await register(formData.email, formData.password, formData.name, formData.referralCode || null, formData.b2bCode || null);
        toast.success(t('auth.accountCreated'));
      }
      // Close dialog on success
      if (onClose) onClose();
      // Navigate to dashboard after successful login
      navigate('/dashboard');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || '';
      const statusCode = error.response?.status;
      // Check if it's an email verification error (403 status or message contains "verify")
      if (statusCode === 403 || errorMessage.toLowerCase().includes('verify')) {
        setShowVerificationError(true);
        toast.error(t('auth.emailNotVerified', 'Please verify your email before logging in.'));
      } else {
        toast.error(errorMessage || t('auth.authFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    if (onClose) onClose();
    setTimeout(() => {
      window.location.href = '/forgot-password';
    }, 100);
  };

  const handleGoogleLogin = () => {
    loginWithGoogleDirect();
    if (onClose) onClose();
  };

  const handleGoogleLoginFallback = () => {
    loginWithGoogle();
    if (onClose) onClose();
  };

  return (
    <>
      {/* Header with gradient */}
      <div className="bg-[#081D33] p-4 sm:p-8 text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#00A9E0]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#E8A317]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        <div className="relative">
          <div className="w-14 h-14 sm:w-20 sm:h-20 bg-white/90 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-2 sm:mb-4 p-1.5 sm:p-2">
            <img src="/assets/logo.png" alt="Freestays" className="h-full w-auto object-contain" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
            {tab === "login" ? t('auth.welcomeBack') : t('auth.joinFreestays')}
          </h2>
          <p className="text-white/70 text-xs sm:text-sm">
            {tab === "login" ? t('auth.signInSubtitle') : t('auth.createSubtitle')}
          </p>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2 bg-gray-100 dark:bg-[#0a2540] rounded-full p-1">
            <TabsTrigger value="login" className="rounded-full text-sm">{t('auth.signIn')}</TabsTrigger>
            <TabsTrigger value="register" className="rounded-full text-sm">{t('auth.createAccount')}</TabsTrigger>
          </TabsList>

          <form onSubmit={handleSubmit} className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
            {tab === "register" && (
              <div>
                <Label htmlFor="name-content" className="text-sm font-medium">{t('auth.fullName')}</Label>
                <Input id="name-content" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder={t('auth.namePlaceholder')} required={tab === "register"} className="h-10 sm:h-12 rounded-xl mt-1" />
              </div>
            )}
            <div>
              <Label htmlFor="email-content" className="text-sm font-medium">{t('auth.emailAddress')}</Label>
              <Input id="email-content" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder={t('auth.emailPlaceholder')} required className="h-10 sm:h-12 rounded-xl mt-1" />
            </div>
            <div>
              <Label htmlFor="password-content" className="text-sm font-medium">{t('auth.password')}</Label>
              <Input id="password-content" type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} placeholder={t('auth.passwordPlaceholder')} required minLength={6} className="h-10 sm:h-12 rounded-xl mt-1" />
              {tab === "login" && (
                <button type="button" onClick={handleForgotPassword} className="text-xs text-primary hover:underline mt-2 block text-right w-full">
                  {t('auth.forgotPassword')}
                </button>
              )}
            </div>
            
            {tab === "register" && (
              <div>
                <Label htmlFor="referralCode-content" className="text-sm font-medium">{t('auth.referralCode')}</Label>
                <Input id="referralCode-content" value={formData.referralCode} onChange={(e) => setFormData({...formData, referralCode: e.target.value.toUpperCase()})} placeholder={t('auth.referralCodePlaceholder')} className="h-10 sm:h-12 rounded-xl mt-1" />
                <p className="text-xs text-muted-foreground mt-1">{t('auth.referralCodeHint')}</p>
              </div>
            )}

            {tab === "register" && (
              <div>
                <Label htmlFor="auth-content-b2b" className="text-sm font-medium flex items-center gap-1.5">
                  <Gift className="w-3.5 h-3.5 text-[#E8A317]" />
                  {t('auth.b2bCode', 'Already have a code?')}
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="auth-content-b2b"
                    value={formData.b2bCode}
                    onChange={(e) => handleB2bCodeChange(e.target.value)}
                    placeholder={t('auth.b2bCodePlaceholder', 'Enter your pass or voucher code')}
                    className="h-10 sm:h-12 rounded-xl pr-10"
                    data-testid="auth-content-b2b-code"
                  />
                  {b2bValidation.status === "loading" && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
                  {b2bValidation.status === "valid" && <Check className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />}
                  {b2bValidation.status === "invalid" && <X className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />}
                </div>
                {b2bValidation.status === "valid" && <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium">{b2bValidation.message}</p>}
                {b2bValidation.status === "invalid" && <p className="text-xs text-red-500 mt-1">{b2bValidation.message}</p>}
                {!b2bValidation.status && <p className="text-xs text-muted-foreground mt-1">{t('auth.b2bCodeHint', 'Enter a voucher or annual pass code you received')}</p>}
              </div>
            )}

            {/* Email Verification Error Message */}
            {showVerificationError && tab === "login" && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      {t('auth.emailNotVerifiedTitle', 'Email Not Verified')}
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      {t('auth.emailNotVerifiedDesc', 'Please check your inbox and click the verification link.')}
                    </p>
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm" 
                      className="mt-2 h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/50"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                    >
                      {resendingVerification ? (
                        <><Loader2 className="w-3 h-3 animate-spin mr-1" /> {t('auth.sending', 'Sending...')}</>
                      ) : (
                        <><Send className="w-3 h-3 mr-1" /> {t('auth.resendVerification', 'Resend Verification Email')}</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <Button type="submit" className="w-full rounded-full h-10 sm:h-12 font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {tab === "login" ? t('auth.signIn') : t('auth.createAccount')}
            </Button>
          </form>

          <div className="relative my-4 sm:my-6">
            <div className="absolute inset-0 flex items-center"><Separator /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-3 text-muted-foreground">{t('auth.orContinueWith')}</span>
            </div>
          </div>

          <Button variant="outline" className="w-full rounded-full h-10 sm:h-12 font-medium hover:bg-secondary/80 transition-all" onClick={handleGoogleLogin} data-testid="google-login-dialog">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Fallback: Emergent Managed Google Auth (backup during transition) */}
          <button
            type="button"
            onClick={handleGoogleLoginFallback}
            className="mt-2 w-full text-[11px] text-muted-foreground hover:text-primary transition-colors underline-offset-2 hover:underline"
            data-testid="google-login-dialog-fallback"
          >
            Trouble signing in? Try alternative Google sign-in
          </button>
        </Tabs>
      </div>
    </>
  );
};

export const AuthDialog = ({ defaultTab = "login" }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(defaultTab);
  const { login, register, loginWithGoogle, loginWithGoogleDirect } = useAuth();
  const { canInstall, isInstalled, handleInstall } = usePWAInstall();
  const [loading, setLoading] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showVerificationError, setShowVerificationError] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", name: "", referralCode: "", b2bCode: "" });
  const [b2bValidation, setB2bValidation] = useState({ status: null, passType: null, message: "" });
  const [b2bTimeout, setB2bTimeout] = useState(null);
  const dialogCloseRef = useRef(null);

  const validateB2bCode = useCallback(async (code) => {
    if (!code || code.trim().length < 3) { setB2bValidation({ status: null, passType: null, message: "" }); return; }
    setB2bValidation({ status: "loading", passType: null, message: "" });
    try {
      const res = await axios.post(`${API}/auth/validate-b2b-code`, { pass_code: code.trim() });
      setB2bValidation(res.data.valid ? { status: "valid", passType: res.data.pass_type, message: res.data.message } : { status: "invalid", passType: null, message: res.data.message });
    } catch { setB2bValidation({ status: "invalid", passType: null, message: "Validation failed" }); }
  }, []);

  const handleB2bCodeChange = (value) => {
    const upper = value.toUpperCase();
    setFormData(prev => ({ ...prev, b2bCode: upper }));
    if (b2bTimeout) clearTimeout(b2bTimeout);
    if (!upper || upper.length < 3) { setB2bValidation({ status: null, passType: null, message: "" }); return; }
    setB2bTimeout(setTimeout(() => validateB2bCode(upper), 600));
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: formData.email });
      toast.success(t('auth.verificationEmailSent', 'Verification email sent! Please check your inbox.'));
      setShowVerificationError(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.resendFailed', 'Failed to resend verification email'));
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowVerificationError(false);
    try {
      if (tab === "login") {
        await login(formData.email, formData.password);
        toast.success(t('auth.welcomeBack'));
      } else {
        await register(formData.email, formData.password, formData.name, formData.referralCode || null, formData.b2bCode || null);
        toast.success(t('auth.accountCreated'));
      }
      // Close dialog on success
      dialogCloseRef.current?.click();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || '';
      const statusCode = error.response?.status;
      // Check if it's an email verification error (403 status or message contains "verify")
      if (statusCode === 403 || errorMessage.toLowerCase().includes('verify')) {
        setShowVerificationError(true);
        toast.error(t('auth.emailNotVerified', 'Please verify your email before logging in.'));
      } else {
        toast.error(errorMessage || t('auth.authFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    // Close the dialog first
    if (dialogCloseRef.current) {
      dialogCloseRef.current.click();
    }
    // Navigate after a small delay to ensure dialog closes
    setTimeout(() => {
      window.location.href = '/forgot-password';
    }, 100);
  };

  return (
    <DialogContent className="sm:max-w-md rounded-3xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
      {/* Hidden close button for programmatic closing */}
      <DialogClose ref={dialogCloseRef} className="hidden" />
      {/* Header with gradient - compact on mobile */}
      <div className="bg-gradient-to-br from-primary to-primary/80 p-4 sm:p-8 text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        <div className="relative">
          <div className="w-14 h-14 sm:w-20 sm:h-20 bg-white/90 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-2 sm:mb-4 p-1.5 sm:p-2">
            <img 
              src="/assets/logo.png" 
              alt="Freestays" 
              className="h-full w-auto object-contain"
            />
          </div>
          <h2 className="font-bold text-xl sm:text-2xl text-primary-foreground mb-1">
            {tab === "login" ? t('auth.welcomeBack') : t('auth.joinFreestays')}
          </h2>
          <p className="text-primary-foreground/80 text-xs sm:text-sm">
            {tab === "login" ? t('auth.signInSubtitle') : t('auth.createSubtitle')}
          </p>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2 bg-secondary/50 rounded-full p-1">
            <TabsTrigger value="login" className="rounded-full text-sm">{t('auth.signIn')}</TabsTrigger>
            <TabsTrigger value="register" className="rounded-full text-sm">{t('auth.createAccount')}</TabsTrigger>
          </TabsList>

          <form onSubmit={handleSubmit} className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
            {tab === "register" && (
              <div>
                <Label htmlFor="name" className="text-sm font-medium">{t('auth.fullName')}</Label>
                <Input 
                  id="name" 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={t('auth.namePlaceholder')}
                  required={tab === "register"}
                  className="h-10 sm:h-12 rounded-xl mt-1"
                  data-testid="input-name"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-sm font-medium">{t('auth.emailAddress')}</Label>
              <Input 
                id="email" 
                type="email"
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder={t('auth.emailPlaceholder')}
                required
                className="h-10 sm:h-12 rounded-xl mt-1"
                data-testid="input-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium">{t('auth.password')}</Label>
              <Input 
                id="password" 
                type="password"
                value={formData.password} 
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder={t('auth.passwordPlaceholder')}
                required
                minLength={6}
                className="h-10 sm:h-12 rounded-xl mt-1"
                data-testid="input-password"
              />
              {tab === "login" && (
                <button 
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-xs text-primary hover:underline mt-2 block text-right w-full"
                >
                  {t('auth.forgotPassword')}
                </button>
              )}
            </div>
            
            {tab === "register" && (
              <div>
                <Label htmlFor="referralCode" className="text-sm font-medium">{t('auth.referralCode')}</Label>
                <Input 
                  id="referralCode" 
                  value={formData.referralCode} 
                  onChange={(e) => setFormData({...formData, referralCode: e.target.value.toUpperCase()})}
                  placeholder={t('auth.referralCodePlaceholder')}
                  className="h-10 sm:h-12 rounded-xl mt-1"
                  data-testid="input-referral-code"
                />
                <p className="text-xs text-muted-foreground mt-1">{t('auth.referralCodeHint')}</p>
              </div>
            )}

            {tab === "register" && (
              <div>
                <Label htmlFor="auth-dialog-b2b" className="text-sm font-medium flex items-center gap-1.5">
                  <Gift className="w-3.5 h-3.5 text-[#E8A317]" />
                  {t('auth.b2bCode', 'Already have a code?')}
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="auth-dialog-b2b"
                    value={formData.b2bCode}
                    onChange={(e) => handleB2bCodeChange(e.target.value)}
                    placeholder={t('auth.b2bCodePlaceholder', 'Enter your pass or voucher code')}
                    className="h-10 sm:h-12 rounded-xl pr-10"
                    data-testid="auth-dialog-b2b-code"
                  />
                  {b2bValidation.status === "loading" && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
                  {b2bValidation.status === "valid" && <Check className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />}
                  {b2bValidation.status === "invalid" && <X className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />}
                </div>
                {b2bValidation.status === "valid" && <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium">{b2bValidation.message}</p>}
                {b2bValidation.status === "invalid" && <p className="text-xs text-red-500 mt-1">{b2bValidation.message}</p>}
                {!b2bValidation.status && <p className="text-xs text-muted-foreground mt-1">{t('auth.b2bCodeHint', 'Enter a voucher or annual pass code you received')}</p>}
              </div>
            )}

            {/* Email Verification Error Message */}
            {showVerificationError && tab === "login" && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      {t('auth.emailNotVerifiedTitle', 'Email Not Verified')}
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      {t('auth.emailNotVerifiedDesc', 'Please check your inbox and click the verification link.')}
                    </p>
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm" 
                      className="mt-2 h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/50"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      data-testid="resend-verification-btn"
                    >
                      {resendingVerification ? (
                        <><Loader2 className="w-3 h-3 animate-spin mr-1" /> {t('auth.sending', 'Sending...')}</>
                      ) : (
                        <><Send className="w-3 h-3 mr-1" /> {t('auth.resendVerification', 'Resend Verification Email')}</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full rounded-full h-10 sm:h-12 font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all" 
              disabled={loading} 
              data-testid="submit-auth"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {tab === "login" ? t('auth.signIn') : t('auth.createAccount')}
            </Button>
          </form>

          <div className="relative my-4 sm:my-6">
            <div className="absolute inset-0 flex items-center"><Separator /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-3 text-muted-foreground">{t('auth.orContinueWith')}</span>
            </div>
          </div>

          <Button 
            variant="outline" 
            className="w-full rounded-full h-10 sm:h-12 font-medium hover:bg-secondary/80 transition-all" 
            onClick={loginWithGoogleDirect} 
            data-testid="google-login"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Fallback: Emergent Managed Google Auth (backup during transition) */}
          <button
            type="button"
            onClick={loginWithGoogle}
            className="mt-2 w-full text-[11px] text-muted-foreground hover:text-primary transition-colors underline-offset-2 hover:underline"
            data-testid="google-login-fallback"
          >
            Trouble signing in? Try alternative Google sign-in
          </button>

          {/* Benefits reminder - hidden on very small screens */}
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-accent/10 rounded-2xl hidden xs:block">
            <p className="text-xs text-center text-muted-foreground mb-2">With a Freestays account you get:</p>
            <div className="flex justify-center gap-3 sm:gap-4 text-xs">
              <span className="flex items-center gap-1 text-primary font-medium">
                <Gift className="w-3.5 h-3.5" /> Free Rooms
              </span>
              <span className="flex items-center gap-1 text-primary font-medium">
                <Percent className="w-3.5 h-3.5" /> No Fees
              </span>
              <span className="flex items-center gap-1 text-primary font-medium">
                <Shield className="w-3.5 h-3.5" /> Secure
              </span>
            </div>
          </div>
          
          {/* PWA Install Prompt */}
          {canInstall && !isInstalled && (
            <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-[#00A9E0]/5 border border-[#00A9E0]/20 rounded-xl">
              {tab === "register" ? (
                // Register tab - Download to start
                <button 
                  onClick={handleInstall}
                  className="w-full flex items-center justify-center gap-2 text-xs sm:text-sm text-primary hover:text-[#00A9E0]/80 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>{t('pwa.downloadToStart', 'Download our Freestays app to get started!')}</span>
                </button>
              ) : (
                // Login tab - Already downloaded?
                <button 
                  onClick={handleInstall}
                  className="w-full flex items-center justify-center gap-2 text-xs sm:text-sm text-primary hover:text-[#00A9E0]/80 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>{t('pwa.alreadyDownloaded', 'Did you download our Freestays app already?')}</span>
                </button>
              )}
            </div>
          )}
        </Tabs>
      </div>
    </DialogContent>
  );
};

export default AuthDialog;
